import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import { useNavigate, useLocation } from "react-router-dom";
import { validateFulfillment, getOriginalAmount, calculateSalesTax, isSalesTaxEnabled, shouldCollectSalesTax, hasOwnerAccess } from "../../common/util";
import FulfillmentEntry from "../../common/component/fulfillment/FulfillmentEntry";
import moment from "moment";
import { UserContext } from "../../context/UserContext";
import LineItems from "../../common/component/lineitem/LineItems";
import { useParams } from "react-router-dom";
import CustomerDetailsButton from "../../common/component/CustomerDetailsButton";
import OrderChat from "../component/OrderChat";
import currency from "currency.js";
import CustomerPayment from "../component/CustomerPayment";
import SalesTax from "../component/SalesTax";

export default function EditOrder(props) {
  const navigate = useNavigate();
  let location = useLocation();
  const { orderId } = useParams();

  const { user } = useContext(UserContext);

  const [order, setOrder] = useState();

  const [items, setItems] = useState([]);
  const [fulfillment, setFulfillment] = useState("");

  const [payment, setPayment] = useState("");
  const [collectSalesTax, setCollectSalesTax] = useState(true);

  const [errors, setErrors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    bkstApi(`/my/order/${orderId}`).then((res) => {
      document.title = res.data.details?.title;
      setOrder(res.data);

      let arr = res.data.items;
      const orderData = res.data;
      // Legacy orders with no collectSalesTax: infer from stored payment (0 = was not collecting).
      const willCollect =
        orderData.settings?.collectSalesTax !== undefined ||
        orderData.collectSalesTax !== undefined ||
        orderData.settings?.includeSalesTax !== undefined
          ? shouldCollectSalesTax(orderData)
          : +orderData.payment?.salesTax > 0;

      // set item price, if missing; hydrate legacy lines missing salesTax when collecting
      for (let o of arr) {
        if (+o.price === +o.total) {
          const unitPrice = currency(o.total).divide(o.quantity || 1).value;
          o.price = getOriginalAmount(unitPrice, o.includeFees, user.config?.fees);
        }
        if (willCollect && o.salesTax?.enabled === undefined) {
          o.salesTax = { enabled: isSalesTaxEnabled(user.config) };
        }
      }
      setItems(arr);

      let o = res.data.fulfillment;
      if (o.type === "delivery") {
        const deliveryFee = res.data.payment.delivery;
        if (deliveryFee) {
          o.option = { delivery: { amount: deliveryFee, includeFees: false, total: deliveryFee } };
        }
      }
      setFulfillment(o);
      setFulfillment(res.data.fulfillment);
      setPayment(res.data.payment);

      setCollectSalesTax(willCollect);

      setLoading(false);
    });
  }, [orderId, location.key]);

  useEffect(() => {
    recalculatePayment();
  }, [items, fulfillment, collectSalesTax]);

  const recalculatePayment = () => {
    const subtotal = currency(items.reduce((acc, o) => +acc + (+o.total || +o.price), 0));
    const delivery = currency(
      (fulfillment?.type === "delivery" && fulfillment?.option?.delivery?.total) || 0
    );

    const discount = currency(payment.discount || 0);

    let salesTax = currency(0);
    if (isSalesTaxEnabled(user.config) && collectSalesTax) {
      salesTax = calculateSalesTax(items, {
        subtotal: subtotal.value,
        discount: discount.value,
        delivery: delivery.value,
        platformFee: payment.platformFee || 0,
        salesTaxConfig: user.config?.salesTax,
      });
    }

    console.log("new subtotal", subtotal.value);
    const total = currency(subtotal)
      .add(payment.tip)
      .add(delivery)
      .add(payment.platformFee)
      .add(salesTax)
      .subtract(discount);
    setPayment({
      ...payment,
      subtotal: subtotal.value,
      delivery: delivery.value,
      salesTax: salesTax.value,
      total: total.value,
    });
  };

  const validate = () => {
    let arr = [];
    console.log("validate", fulfillment);
    if (items.length === 0) {
      arr.push("Items are missing");
    }
    arr = arr.concat(validateFulfillment(fulfillment));

    return arr;
  };

  const saveOrderChanges = (e) => {
    e.preventDefault();
    const arr = validate();
    setErrors(arr);

    if (arr.length === 0) {
      setProcessing(true);
      bkstApi
        .put(`/my/order/${orderId}`, {
          items,
          fulfillment,
          payment,
          settings: { collectSalesTax },
          updTs: order.updTs,
        })
        .then((res) => {
          navigate(`/order/${orderId}`);
        })
        .catch((err) => {
          setProcessing(false);
          setErrors([err.response?.data?.message || "An error occurred while saving changes"]);
        });
    }
  };

  console.log("RENDER >>>>");

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Box sx={{ backgroundColor: "#FBEEE6", minHeight: "100vh" }}>
        <Container maxWidth="md">
          <Box pt={4} pb={30}>
            <Box>
              <Box mb={4}>
                <Typography variant="h1" gutterBottom>
                  Edit Order
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Make your changes, then click the Save button to update this order
                </Typography>
              </Box>
            </Box>
            <Box>
              <Grid container alignItems="center">
                <Grid item xs={6}>
                  {order.customer.id && <CustomerDetailsButton customer={order.customer} />}
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {order.paymentType !== "offline" && (
                      <OrderChat order={order} disableAddImageToItem />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <LineItems items={items} onChange={(arr) => setItems(arr)} />
              {items.length > 0 && (
                <Box>
                  <Box>
                    <Box my={4}></Box>
                    <Typography variant="overline" color="textSecondary">
                      FULFILLMENT
                    </Typography>
                    <Paper elevation={0} sx={{ minWidth: 0 }}>
                      <Box px={2} pt={2} pb={2} sx={{ minWidth: 0 }}>
                        <FulfillmentEntry
                          fulfillment={fulfillment}
                          onChange={(o) => setFulfillment(o)}
                        />
                        {order.fulfillment.type === "delivery" &&
                          order.fulfillment.provider?.name !== "in-house" && (
                            <Box mt={2}>
                              <Alert severity="info">
                                Updating fulfillment details for an order with 3rd party delivery
                                will result in the cancelation of your existing delivery
                                (cancelation fee may apply) & creation of a new one.
                              </Alert>
                            </Box>
                          )}
                      </Box>
                    </Paper>
                  </Box>
                  <Box my={4}></Box>
                  <Typography variant="overline" color="textSecondary">
                    PAYMENT SUMMARY
                  </Typography>
                  <Paper elevation={0}>
                    <Box p={2}>
                      {isSalesTaxEnabled(user.config) &&
                        (hasOwnerAccess(user.role) || user.role === "manager") && (
                          <Box mb={2}>
                            <SalesTax
                              include={collectSalesTax}
                              onChange={setCollectSalesTax}
                            />
                          </Box>
                        )}
                      <CustomerPayment payment={payment} />
                      {+payment.total !== +order.payment.total && (
                        <Box pt={1} mt={2} sx={{ borderTop: "1px solid #EEE" }}>
                          <Grid container spacing={1}>
                            <Grid item xs={9}>
                              <Typography variant="overline" color="textSecondary">
                                TOTAL (OLD)
                              </Typography>
                            </Grid>
                            <Grid item xs={3} align="right">
                              <Typography variant="overline" color="textSecondary">
                                {currency(order.payment.total).format()}
                              </Typography>
                            </Grid>
                            <Grid item xs={9}>
                              <Typography variant="h6" sx={{ fontWeight: "700" }}>
                                Change in Total
                              </Typography>
                            </Grid>
                            <Grid item xs={3} align="right">
                              <Typography variant="h6" sx={{ fontWeight: "700" }}>
                                {currency(+payment.total - +order.payment.total).format()}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      <Box mt={2}>
                        {+payment.total - +order.payment.total > 0 && (
                          <Alert severity="info">
                            Since your changes will increase the order total, the amount will be
                            added to the balance due & charged to the customer's card on file based
                            on the payment schedule.
                          </Alert>
                        )}
                        {+payment.total - +order.payment.total < 0 && (
                          <Alert severity="warning">
                            Since your changes will reduce the order total, the balance due will be
                            reduced & any leftover amount will be refunded.
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                  {errors && errors.length > 0 && (
                    <Box my={2}>
                      <Alert severity="error">{errors.join(". ")}</Alert>
                    </Box>
                  )}
                  <Box my={2} sx={{ display: "flex", gap: "15px" }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={() => navigate(`/order/${orderId}`)}
                    >
                      CANCEL CHANGES
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={saveOrderChanges}
                    >
                      SAVE CHANGES
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Backdrop open={processing} style={{ zIndex: "1000", color: "#fff" }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </Container>
      </Box>
    );
  }
}
